// --- Store for Managing SDK State ---
import { createDebugLogger } from './utils/debug';
const debug = createDebugLogger('store');
export class AdpxStore {
    constructor() {
        this.showListeners = new Set();
        this.pendingShow = false;
        this.modalCloseCallback = null;
        /** Shadow root owned by the SDK instance; used to scope DOM lookups per instance. */
        this.shadowRoot = null;
        this.state = {
            offers: [],
            settings: null,
            styles: null,
            sessionId: null,
            isLoading: false,
            error: null,
            fingerprint: null,
        };
        this.listeners = new Map();
    }
    setShadowRoot(shadowRoot) {
        this.shadowRoot = shadowRoot;
    }
    getShadowRoot() {
        return this.shadowRoot;
    }
    // State getters
    getState() {
        return { ...this.state };
    }
    getOffers() {
        return [...this.state.offers];
    }
    getOffer(index) {
        return this.state.offers[index] || null;
    }
    getOfferById(id) {
        return this.state.offers.find(offer => offer.id === id) || null;
    }
    getSettings() {
        return this.state.settings ? { ...this.state.settings } : null;
    }
    getStyles() {
        return this.state.styles ? { ...this.state.styles } : null;
    }
    getSessionId() {
        return this.state.sessionId;
    }
    getFingerprint() {
        return this.state.fingerprint;
    }
    isLoadingOffers() {
        return this.state.isLoading;
    }
    getError() {
        return this.state.error;
    }
    getTotalOffers() {
        return this.state.offers.length;
    }
    // State setters
    setOffers(offers) {
        this.state.offers = [...offers];
        this.emit('offers-updated');
        this.emit('change');
    }
    setSettings(settings) {
        this.state.settings = { ...settings };
        this.emit('settings-updated');
        this.emit('change');
    }
    updateSettings(settingsUpdate) {
        if (this.state.settings) {
            this.state.settings = { ...this.state.settings, ...settingsUpdate };
        }
        else {
            this.state.settings = { ...settingsUpdate };
        }
        this.emit('settings-updated');
        this.emit('change');
    }
    setStyles(styles) {
        this.state.styles = { ...styles };
        this.emit('change');
    }
    updateStyles(stylesUpdate) {
        if (this.state.styles) {
            this.state.styles = { ...this.state.styles, ...stylesUpdate };
        }
        else {
            this.state.styles = { ...stylesUpdate };
        }
        this.emit('change');
    }
    setSessionId(sessionId) {
        this.state.sessionId = sessionId;
        this.emit('change');
    }
    setFingerprint(fingerprint) {
        this.state.fingerprint = fingerprint;
        this.emit('change');
    }
    setLoading(isLoading) {
        this.state.isLoading = isLoading;
        this.emit('change');
    }
    setError(error) {
        this.state.error = error;
        if (error) {
            this.emit('error');
        }
        this.emit('change');
    }
    // Reset methods
    reset() {
        this.state = {
            offers: [],
            settings: null,
            styles: null,
            sessionId: null,
            isLoading: false,
            error: null,
            fingerprint: this.state.fingerprint, // Keep fingerprint across resets
        };
        this.pendingShow = false;
        this.emit('change');
    }
    resetOffersOnly() {
        this.state.offers = [];
        this.state.error = null;
        this.pendingShow = false;
        this.emit('offers-updated');
        this.emit('change');
    }
    requestShow() {
        if (this.showListeners.size === 0) {
            this.pendingShow = true;
            return;
        }
        this.pendingShow = false;
        this.showListeners.forEach(listener => {
            try {
                listener();
            }
            catch (error) {
                debug.error('Error in show listener:', error);
            }
        });
    }
    on(event, listener) {
        if (event === 'show') {
            this.showListeners.add(listener);
            if (this.pendingShow) {
                this.pendingShow = false;
                try {
                    listener();
                }
                catch (error) {
                    debug.error('Error in show listener:', error);
                }
            }
            return;
        }
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(listener);
    }
    off(event, listener) {
        if (event === 'show') {
            this.showListeners.delete(listener);
            return;
        }
        this.listeners.get(event)?.delete(listener);
    }
    once(event, listener) {
        const onceListener = (state) => {
            listener(state);
            this.off(event, onceListener);
        };
        this.on(event, onceListener);
    }
    emit(event) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach(listener => {
                try {
                    listener(this.getState());
                }
                catch (error) {
                    debug.error(`Error in ${event} listener:`, error);
                }
            });
        }
    }
    // Utility methods
    hasOffers() {
        return this.state.offers.length > 0;
    }
    hasSettings() {
        return this.state.settings !== null;
    }
    hasStyles() {
        return this.state.styles !== null;
    }
    isReady() {
        return this.hasOffers() && this.hasSettings() && !this.state.isLoading;
    }
    // Serialization for debugging/persistence
    toJSON() {
        return JSON.stringify(this.state, null, 2);
    }
    fromJSON(json) {
        try {
            const parsedState = JSON.parse(json);
            this.state = { ...this.state, ...parsedState };
            this.emit('change');
        }
        catch (error) {
            debug.error('Error parsing JSON state:', error);
            this.setError('Failed to restore state from JSON');
        }
    }
    // Modal close callback methods
    setModalCloseCallback(callback) {
        this.modalCloseCallback = callback;
    }
    clearModalCloseCallback() {
        this.modalCloseCallback = null;
    }
    closeModal() {
        return new Promise(resolve => {
            // Scope DOM queries to this instance's shadow root so two SDK instances
            // can coexist without stomping each other's modals.
            const root = this.shadowRoot ?? document;
            const modalContainer = root.getElementById('adpx_modal-overlay');
            const modalContentContainer = root.getElementById('adpx-modal-container');
            if (modalContainer) {
                modalContainer.remove();
                modalContentContainer?.remove();
            }
            if (this.modalCloseCallback) {
                this.modalCloseCallback();
                setTimeout(resolve, 100);
            }
            else {
                debug.warn('No modal close callback registered');
                resolve();
            }
        });
    }
}
